import React from 'react';
import { Trash2, Baby, UserRound, Mail } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

export const CHILD_MAX_AGE = 13;   // "under 14": 0-13 = child (free); 14+ = adult (pays)

export function ageFromDob(dob) {
  if (!dob) return null;
  const d = new Date(dob + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return null;
  const t = new Date();
  let a = t.getFullYear() - d.getFullYear();
  if (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate())) a -= 1;
  return a;
}

export const isChildDob = (dob) => { const a = ageFromDob(dob); return a !== null && a >= 0 && a <= CHILD_MAX_AGE; };

export const emptyFamilyMember = () => ({ _uiId: crypto.randomUUID(), name: '', dob: '', gender: '', email: '', share_parent_email: true, membership_type: 'standard' });

/** Validate one row client-side (server is authoritative). Returns an error string or null. */
export function validateFamilyMember(fm, idx, parentEmail, otherEmails) {
  const n = idx + 1;
  if (!fm.name.trim() || !fm.dob || !fm.gender) return `Family member ${n}: please complete the name, date of birth and gender`;
  const age = ageFromDob(fm.dob);
  if (age === null || age < 0) return `Family member ${n}: please check the date of birth`;
  const child = age <= CHILD_MAX_AGE;
  const em = (fm.email || '').trim().toLowerCase();
  const pe = (parentEmail || '').trim().toLowerCase();
  if (child) {
    if (!fm.share_parent_email) {
      if (!em || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) return `Family member ${n}: please enter an email address for ${fm.name.trim()}, or choose to use your own`;
    }
  } else {
    if (!em || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) return `Family member ${n}: please provide an email address for ${fm.name.trim()}, so their card and news reach them directly`;
    if (em === pe || otherEmails.includes(em)) return `Family member ${n}: please use a different email address for ${fm.name.trim()} — each adult member receives their own communications`;
  }
  return null;
}

export function serialiseFamilyMember(fm) {
  const child = isChildDob(fm.dob);
  return {
    name: fm.name.trim(), dob: fm.dob, gender: fm.gender,
    email: child && fm.share_parent_email ? '' : (fm.email || '').trim().toLowerCase(),
    share_parent_email: child ? !!fm.share_parent_email : false,
    membership_type: child ? 'child_under14' : (fm.membership_type || 'standard'),
  };
}

/**
 * One family member row. Age is derived from DOB:
 *  - under 14 -> FREE child; may share the parent's email or use their own
 *  - 14+      -> adult who buys their own membership (type select) with their own email
 */
export function FamilyMemberRow({ fm, idx, onChange, onRemove, parentEmail, plans = [], testPrefix = 'family-member', showRemove = true, title }) {
  const age = ageFromDob(fm.dob);
  const known = age !== null && age >= 0;
  const child = known && age <= CHILD_MAX_AGE;
  const adultPlans = plans.filter(p => !p.min_age || (known && age >= p.min_age));
  const set = (patch) => onChange({ ...fm, ...patch });
  const inputCls = 'w-full h-10 rounded-lg border border-[hsl(var(--input))] px-3 text-sm bg-white';
  const selectedPlan = plans.find(p => p.type === fm.membership_type) || adultPlans[0];

  return (
    <div className="p-3 mb-3 rounded-lg border border-[hsl(var(--lux-gold)/0.25)] bg-[hsl(var(--lux-ivory))] min-w-0 overflow-hidden" data-testid={`${testPrefix}-row-${idx}`}>
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <span className="text-sm font-semibold text-[hsl(var(--lux-royal))]">{title || `Family member ${idx + 1}`}</span>
        <div className="flex items-center gap-2">
          {known && (
            child ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2.5 py-0.5 text-xs font-medium" data-testid={`${testPrefix}-age-badge-${idx}`}>
                <Baby className="w-3.5 h-3.5" /> Child · age {age} · Complimentary
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--lux-royal-soft))] text-[hsl(var(--lux-royal))] border border-[hsl(var(--lux-gold)/0.4)] px-2.5 py-0.5 text-xs font-medium" data-testid={`${testPrefix}-age-badge-${idx}`}>
                <UserRound className="w-3.5 h-3.5" /> Adult · age {age} · {selectedPlan ? selectedPlan.price : 'membership'}
              </span>
            )
          )}
          {showRemove && (
            <button type="button" onClick={onRemove} className="text-[hsl(var(--lux-vermilion))] hover:opacity-70 transition-opacity" aria-label="Remove family member" data-testid={`remove-${testPrefix}-${idx}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0 [&>*]:min-w-0">
        <div>
          <Label className="text-xs">Full Name *</Label>
          <Input value={fm.name} onChange={e => set({ name: e.target.value })} data-testid={`${testPrefix}-name-${idx}`} />
        </div>
        <div>
          <Label className="text-xs">Date of Birth *</Label>
          <Input type="date" className="w-full min-w-0" value={fm.dob} max={new Date().toISOString().split('T')[0]} onChange={e => set({ dob: e.target.value })} data-testid={`${testPrefix}-dob-${idx}`} />
        </div>
        <div>
          <Label className="text-xs">Gender *</Label>
          <select value={fm.gender} onChange={e => set({ gender: e.target.value })} className={inputCls} data-testid={`${testPrefix}-gender-${idx}`}>
            <option value="">Select gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other / prefer not to say</option>
          </select>
        </div>

        {!known && (
          <div className="text-xs text-[hsl(var(--lux-ink)/0.6)] self-end pb-2" data-testid={`${testPrefix}-dob-hint-${idx}`}>
            Please enter the date of birth and we will show the options available for this family member.
          </div>
        )}

        {known && child && (
          <div className="sm:col-span-2 space-y-2" data-testid={`${testPrefix}-child-email-${idx}`}>
            <Label className="text-xs flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> How should we reach {fm.name.trim() || 'this child'}?</Label>
            <div className="flex flex-col sm:flex-row gap-2">
              <label className={`flex-1 min-w-0 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer ${fm.share_parent_email ? 'border-[hsl(var(--lux-royal))] bg-white' : 'border-[hsl(var(--input))]'}`}>
                <input type="radio" name={`${testPrefix}-email-mode-${idx}`} checked={!!fm.share_parent_email} onChange={() => set({ share_parent_email: true })} className="accent-[hsl(var(--lux-royal))] shrink-0" data-testid={`${testPrefix}-share-email-${idx}`} />
                <span className="min-w-0">Through my email{parentEmail ? <span className="block text-xs text-[hsl(var(--lux-ink)/0.55)] break-all">{parentEmail}</span> : null}</span>
              </label>
              <label className={`flex-1 min-w-0 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer ${!fm.share_parent_email ? 'border-[hsl(var(--lux-royal))] bg-white' : 'border-[hsl(var(--input))]'}`}>
                <input type="radio" name={`${testPrefix}-email-mode-${idx}`} checked={!fm.share_parent_email} onChange={() => set({ share_parent_email: false })} className="accent-[hsl(var(--lux-royal))] shrink-0" data-testid={`${testPrefix}-own-email-${idx}`} />
                <span>They have their own email address</span>
              </label>
            </div>
            {!fm.share_parent_email && (
              <Input type="email" placeholder="Their email address" value={fm.email} onChange={e => set({ email: e.target.value })} data-testid={`${testPrefix}-email-${idx}`} />
            )}
            <p className="text-xs text-[hsl(var(--lux-ink)/0.6)]">Membership for children under 14 is <strong>complimentary</strong>. {fm.name.trim() || 'Your child'} will receive their own membership card, and should it ever be checked at an event, our team will be able to reach you as parent or guardian. The child membership continues alongside yours until their 14th birthday.</p>
          </div>
        )}

        {known && !child && (
          <>
            <div>
              <Label className="text-xs">Email address *</Label>
              <Input type="email" value={fm.email} onChange={e => set({ email: e.target.value })} data-testid={`${testPrefix}-email-${idx}`} />
            </div>
            <div>
              <Label className="text-xs">Membership for {fm.name.trim() || 'this family member'} *</Label>
              <select value={fm.membership_type || 'standard'} onChange={e => set({ membership_type: e.target.value })} className={inputCls} data-testid={`${testPrefix}-type-${idx}`}>
                {adultPlans.map(p => <option key={p.type} value={p.type}>{p.label} — {p.price}{p.period ? ` (${p.period})` : ''}</option>)}
              </select>
            </div>
            <p className="sm:col-span-2 text-xs text-[hsl(var(--lux-ink)/0.6)]">Family members aged 14 and over join as members in their own right, with their own card, tickets and news sent to their email address. Their membership is included in today's payment.</p>
          </>
        )}
      </div>
    </div>
  );
}
